/**
 * «Дальше по плану» — шаг маршрута мастерства, который выбрал сервер:
 * новый урок, закрепление, проверка знаний или финиш. Номера урока и
 * «сколько осталось до ступени» здесь нет: ученик просто идёт по шагам.
 */

import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import type { NextStep, SkillNodeView } from '@aptile/shared';

import { Button } from '../Button';
import { IconBolt, IconRefresh, IconSparkles, IconTrophy, type AppIcon } from '../icons';
import { useUserStore } from '../../store/userStore';
import { PrefetchDot } from './PrefetchDot';

/** Куда ведёт шаг; `null` — идти некуда (программа пройдена). */
export function stepPath(step: NextStep): string | null {
  switch (step.kind) {
    case 'LESSON':
      return `/lesson/${step.skillId}`;
    case 'REVIEW':
      return '/practice/review';
    case 'CHECK':
      return '/practice/check';
    case 'DONE':
      return null;
  }
}

/** Подпись кнопки перехода к шагу. */
export function stepButtonKey(step: NextStep) {
  switch (step.kind) {
    case 'LESSON':
      return 'next.lesson.button' as const;
    case 'REVIEW':
      return 'next.review.button' as const;
    case 'CHECK':
      return 'next.check.button' as const;
    case 'DONE':
      return 'next.done.label' as const;
  }
}

function reviewTitleKey(reason: Extract<NextStep, { kind: 'REVIEW' }>['reason']) {
  switch (reason) {
    case 'MEMORY_DEBT':
      return 'next.review.title.MEMORY_DEBT' as const;
    case 'CONSOLIDATE':
      return 'next.review.title.CONSOLIDATE' as const;
    case 'LEVEL_GAPS':
      return 'next.review.title.LEVEL_GAPS' as const;
  }
}

interface Content {
  readonly icon: AppIcon;
  readonly label: string;
  readonly title: string;
  readonly text: string;
  readonly tone: string;
}

export function NextStepCard({ step, skills, compact = false }: { readonly step: NextStep; readonly skills: readonly SkillNodeView[]; readonly compact?: boolean }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const userId = useUserStore((state) => state.userId);
  const path = stepPath(step);
  const lesson = step.kind === 'LESSON' ? skills.find((skill) => skill.skillId === step.skillId) : step.kind === 'CHECK' ? skills.find((skill) => skill.skillId === step.skillIds[0]) : undefined;

  const content: Content =
    step.kind === 'LESSON'
      ? { icon: IconSparkles, label: t('next.lesson.label'), title: lesson?.title ?? t('next.lesson.label'), text: lesson === undefined ? '' : `${t('map.topic')} ${lesson.grammarFocus}`, tone: 'text-accent-dark' }
      : step.kind === 'REVIEW'
        ? { icon: IconRefresh, label: t('next.review.label'), title: t(reviewTitleKey(step.reason)), text: t('next.review.text', { phrases: step.phrases }), tone: 'text-teal-deep' }
        : step.kind === 'CHECK'
          ? { icon: IconBolt, label: t('next.check.label'), title: t('next.check.title'), text: t('next.check.text'), tone: 'text-accent-dark' }
          : { icon: IconTrophy, label: t('next.done.label'), title: t('next.done.title'), text: t('next.done.text'), tone: 'text-correct' };
  const Icon = content.icon;

  return (
    <motion.section
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ type: 'spring', stiffness: 260, damping: 26 }}
      aria-label={t('map.nextUp')}
      className={
        compact
          ? 'flex flex-col gap-3'
          : 'glass relative flex flex-col gap-4 overflow-hidden rounded-card border border-accent/40 p-5 shadow-card-lg sm:p-6 lg:p-8'
      }
    >
      {!compact && <span aria-hidden="true" className="pointer-events-none absolute -right-12 -top-12 h-40 w-40 rounded-full bg-accent/20 blur-3xl" />}
      <span className={`t-eyebrow relative flex items-center gap-2 ${content.tone}`}>
        <Icon size={16} aria-hidden="true" /> {content.label}
      </span>
      <h2 className="relative t-h1">{content.title}</h2>
      {content.text.length > 0 && <p className="relative text-sm leading-snug text-muted">{content.text}</p>}
      {path !== null && (
        <Button fullWidth magnetic onClick={() => navigate(path)}>
          <span className="inline-flex items-center justify-center">
            {t(stepButtonKey(step))}
            {step.kind === 'LESSON' && <PrefetchDot userId={userId} skillId={step.skillId} />}
          </span>
        </Button>
      )}
      {/* Проверку можно не сдавать — тогда просто следующий урок. */}
      {step.kind === 'CHECK' && (
        <Button fullWidth variant="ghost" onClick={() => navigate(`/lesson/${step.skillIds[0] ?? ''}`)}>
          {t('next.check.skip')}
        </Button>
      )}
    </motion.section>
  );
}
